import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { Icon } from '@iconify/react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useInstitution } from '@/contexts/InstitutionContext'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import { ApiClientError } from '@/lib/api/client'
import {
  fetchCampaignGamification,
  updateCampaignGamification,
  updateCampaignSpecialSupporterTarget,
  type CampaignGamification,
  EMPTY_ACHIEVEMENT_REWARDS,
} from '@/lib/campaign-gamification'
import { achievementScope, type LandingAchievementId } from '@/lib/landing-achievements'
import styles from './GamificacaoTab.module.css'
import type { CampaignEditorSaveTask } from './editor/campaignEditorSaveTask'
import { CampaignAchievementRecipients } from './CampaignAchievementRecipients'
import {
  DEFAULT_SCRATCH_CONFIG,
  validateScratchConfig,
  type ScratchCardConfig,
} from '@/lib/campaign-scratch-card'
import { ScratchCardConfigEditor } from './ScratchCardConfigEditor'
import { ScratchCardRecipients } from './ScratchCardRecipients'
import { AchievementRewardsEditor } from './AchievementRewardsEditor'
import { MonthlyMedalsAdmin } from './MonthlyMedalsAdmin'

const BADGES: {
  id: LandingAchievementId
  number: number
  name: string
  milestone: string
  image: string
}[] = [
  {
    id: 'first_purchase',
    number: 1,
    name: 'Primeiro passo',
    milestone: '1 compra paga',
    image: '/conquistas%20doa%C3%A7%C3%B5es/01%20primeira%20compra.png',
  },
  {
    id: 'frequent_supporter',
    number: 2,
    name: 'Apoiador frequente',
    milestone: '2 compras pagas',
    image: '/conquistas%20doa%C3%A7%C3%B5es/02%20comprador%20frequente.png',
  },
  {
    id: 'special_supporter',
    number: 3,
    name: 'Apoiador especial',
    milestone: '',
    image: '/conquistas%20doa%C3%A7%C3%B5es/03%20comprador%20especial.png',
  },
  {
    id: 'profile_complete',
    number: 14,
    name: 'Perfil completo',
    milestone: 'Preencha 100% do seu perfil',
    image: '/conquistas%20doa%C3%A7%C3%B5es/14%20perfil%20completo.png',
  },
  {
    id: 'avatar_customized',
    number: 17,
    name: 'Avatar personalizado',
    milestone: 'Personalize e salve seu avatar nesta campanha',
    image: '/conquistas%20doa%C3%A7%C3%B5es/17%20avatar%20personalizado.png',
  },
  {
    id: 'scratch_card',
    number: 21,
    name: 'Raspadinha',
    milestone: 'Uma surpresa gratuita para cada participante',
    image: '/conquistas%20doa%C3%A7%C3%B5es/21%20Raspadinha.png',
  },
  {
    id: 'roulette',
    number: 22,
    name: 'Roleta',
    milestone: 'Use a roleta desta campanha pelo menos uma vez',
    image: '/conquistas%20doa%C3%A7%C3%B5es/22%20roleta.png',
  },
  {
    id: 'calendar',
    number: 23,
    name: 'Calendário',
    milestone: 'Acesse o perfil em dias seguidos, com o cadastro completo',
    image: '/conquistas%20doa%C3%A7%C3%B5es/23%20calendario.png',
  },
]

export function GamificacaoTab({
  campaignId,
  onSaveStateChange,
  onSaved,
}: {
  campaignId: number
  onSaveStateChange?: (state: CampaignEditorSaveTask | null) => void
  onSaved?: () => void
}) {
  const { token, guard } = useAuth()
  const { institutionId } = useInstitution()
  const { runWithStepUp } = useCampaignTotpStepUp()
  const [messages, setMessages] = useState<CampaignGamification['achievements'] | null>(
    null
  )
  const loadKey = `${campaignId}:${guard}:${institutionId ?? ''}`
  const [loadedKey, setLoadedKey] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [expanded, setExpanded] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null)
  const [scratch, setScratch] = useState<ScratchCardConfig>(DEFAULT_SCRATCH_CONFIG)
  const [savedScratch, setSavedScratch] = useState(JSON.stringify(DEFAULT_SCRATCH_CONFIG))
  const [targetAmount, setTargetAmount] = useState('1000.00')
  const [savedTargetAmount, setSavedTargetAmount] = useState('1000.00')
  const [targetNotice, setTargetNotice] = useState<string | null>(null)
  const saveRef = useRef<() => Promise<boolean>>(async () => false)
  const triggerSave = useCallback(() => saveRef.current(), [])
  const hasChanges =
    loadedKey === loadKey &&
    savedSnapshot !== null &&
    (JSON.stringify(messages) !== savedSnapshot ||
      JSON.stringify(scratch) !== savedScratch ||
      (targetAmount || '1000.00') !== savedTargetAmount)

  useEffect(() => {
    onSaveStateChange?.({ hasChanges, saving, save: triggerSave })
  }, [hasChanges, onSaveStateChange, saving, triggerSave])
  useEffect(() => () => onSaveStateChange?.(null), [onSaveStateChange])

  useEffect(() => {
    if (guard === 'admin' && !institutionId) return
    let active = true
    fetchCampaignGamification(campaignId, token ?? undefined)
      .then((result) => {
        if (active) {
          const achievements = {
            ...result.achievements,
            roulette: result.achievements.roulette ?? {
              title: 'Roleta',
              text: null,
              video_url: null,
            },
            calendar: result.achievements.calendar ?? {
              title: 'Presença que faz a diferença',
              text: null,
              video_url: null,
              target_days: 7,
            },
            scratch_card: result.achievements.scratch_card ?? {
              title: 'Você revelou uma surpresa!',
              text: null,
              video_url: null,
            },
          }
          setMessages(achievements)
          setSavedSnapshot(JSON.stringify(achievements))
          setScratch(result.scratch_card ?? DEFAULT_SCRATCH_CONFIG)
          setSavedScratch(JSON.stringify(result.scratch_card ?? DEFAULT_SCRATCH_CONFIG))
          setTargetAmount(result.special_supporter_target_amount ?? '1000.00')
          setSavedTargetAmount(result.special_supporter_target_amount ?? '1000.00')
          setTargetNotice(null)
          setError(null)
        }
      })
      .catch((cause) => {
        if (active) {
          setMessages(null)
          setError(errorMessage(cause))
        }
      })
      .finally(() => {
        if (active) setLoadedKey(loadKey)
      })
    return () => {
      active = false
    }
  }, [campaignId, guard, institutionId, loadKey, token])

  const updateField = (
    id: LandingAchievementId,
    field: 'title' | 'text' | 'video_url' | 'locked_reason',
    value: string
  ) => {
    setMessages((current) =>
      current
        ? {
            ...current,
            [id]: { ...current[id], [field]: value },
          }
        : current
    )
    setSaved(false)
    setError(null)
  }

  const save = async (): Promise<boolean> => {
    if (!messages || saving) return false
    const targetDays = messages.calendar.target_days ?? 7
    if (!Number.isInteger(targetDays) || targetDays < 1 || targetDays > 365) {
      setExpanded((current) => [...new Set([...current, 'calendar'])])
      setError('Informe de 1 a 365 dias seguidos para conquistar o selo Calendário.')
      return false
    }
    const scratchError = validateScratchConfig(scratch)
    if (scratchError) {
      setExpanded((current) => [...new Set([...current, 'scratch_card'])])
      setError(scratchError)
      return false
    }
    if (!validTargetAmount(targetAmount)) {
      setExpanded((current) => [...new Set([...current, 'special_supporter'])])
      setError('Informe um valor positivo, com no máximo duas casas decimais.')
      return false
    }
    if (BADGES.some(({ id }) => !messages[id]?.title.trim())) {
      setExpanded((current) => [
        ...new Set([
          ...current,
          ...BADGES.filter(({ id }) => !messages[id]?.title.trim()).map(({ id }) => id),
        ]),
      ])
      setError('Preencha o título de cada selo antes de salvar.')
      return false
    }
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      const result = await runWithStepUp(() =>
        updateCampaignGamification(
          campaignId,
          {
            ...messages,
            ...Object.fromEntries(
              BADGES.map(({ id }) => [
                id,
                {
                  enabled: messages[id].enabled === true,
                  ...(id === 'calendar' ? { target_days: targetDays } : {}),
                  rewards: messages[id].rewards ?? EMPTY_ACHIEVEMENT_REWARDS,
                  title: messages[id].title.trim(),
                  text: messages[id].text?.trim() || null,
                  video_url: messages[id].video_url?.trim() || null,
                  locked_reason: messages[id].locked_reason?.trim() || null,
                },
              ])
            ),
          } as CampaignGamification['achievements'],
          token ?? undefined,
          targetAmount || '1000.00',
          scratch
        )
      )
      const achievements = {
        ...result.achievements,
        roulette: result.achievements.roulette ?? messages.roulette,
        calendar: result.achievements.calendar ?? messages.calendar,
        scratch_card: result.achievements.scratch_card ?? messages.scratch_card,
      }
      setMessages(achievements)
      setSavedSnapshot(JSON.stringify(achievements))
      setScratch(result.scratch_card ?? scratch)
      setSavedScratch(JSON.stringify(result.scratch_card ?? scratch))
      setTargetAmount(result.special_supporter_target_amount ?? '1000.00')
      setSavedTargetAmount(result.special_supporter_target_amount ?? '1000.00')
      setTargetNotice(null)
      setSaved(true)
      return true
    } catch (cause) {
      setError(errorMessage(cause))
      return false
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    saveRef.current = save
  })

  const saveTarget = async () => {
    if (saving || !validTargetAmount(targetAmount)) return
    setSaving(true)
    setTargetNotice(null)
    try {
      const result = await runWithStepUp(() =>
        updateCampaignSpecialSupporterTarget(
          campaignId,
          targetAmount || '1000.00',
          token ?? undefined
        )
      )
      const amount = result.special_supporter_target_amount ?? '1000.00'
      setTargetAmount(amount)
      setSavedTargetAmount(amount)
      setTargetNotice('Valor do selo salvo com sucesso!')
      onSaved?.()
    } catch (cause) {
      setTargetNotice(errorMessage(cause))
    } finally {
      setSaving(false)
    }
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    void save().then((saved) => {
      if (saved) onSaved?.()
    })
  }

  if (guard === 'admin' && !institutionId) {
    return (
      <div className={styles.empty}>
        <h2>Selecione uma instituição</h2>
        <p>Selecione uma instituição para personalizar os selos desta campanha.</p>
        <Link to="/campanhas">Ver campanhas</Link>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <header className={styles.hero}>
        <div className={styles.heroIcon}>
          <Icon icon="mdi:star-four-points" aria-hidden />
        </div>
        <div>
          <p className={styles.eyebrow}>Jornada do apoiador</p>
          <h2>Gamificação</h2>
          <p>
            Personalize o convite para conquistar cada selo e a surpresa que o apoiador
            verá depois da conquista.
          </p>
        </div>
      </header>

      {loadedKey !== loadKey ? (
        <p role="status" className={styles.status}>
          Carregando selos...
        </p>
      ) : messages ? (
        <form onSubmit={submit} className={styles.form}>
          {(['account', 'campaign'] as const).map((scope) => (
            <section
              key={scope}
              className={styles.categoryContainer}
              aria-labelledby={`admin-badges-${scope}`}
            >
              <header className={styles.categoryHeader}>
                <h3 id={`admin-badges-${scope}`}>
                  {scope === 'account' ? 'Selos da conta' : 'Selos da campanha'}
                </h3>
                <span>
                  {BADGES.filter((badge) => achievementScope(badge.id) === scope).length}{' '}
                  selos
                </span>
                <p>
                  {scope === 'account'
                    ? 'Acompanham o usuário em todas as campanhas. As mensagens e recompensas configuradas aqui valem para esta campanha.'
                    : 'Conquistas exclusivas da campanha atual.'}
                </p>
              </header>
              <div className={styles.categoryPanels}>
                {BADGES.filter((badge) => achievementScope(badge.id) === scope).map(
                  ({ id, number, name, milestone, image }) => (
                    <section
                      className={styles.panel}
                      key={id}
                      aria-labelledby={`badge-${id}`}
                    >
                      <div className={styles.panelHeader}>
                        <button
                          type="button"
                          className={styles.expandButton}
                          aria-expanded={expanded.includes(id)}
                          aria-controls={`badge-panel-${id}`}
                          onClick={() =>
                            setExpanded((current) =>
                              current.includes(id)
                                ? current.filter((item) => item !== id)
                                : [...current, id]
                            )
                          }
                        >
                          <img src={image} alt="" />
                          <span>
                            <small>SELO {String(number).padStart(2, '0')}</small>
                            <strong id={`badge-${id}`}>{name}</strong>
                            <span>
                              {messages[id].enabled === true
                                ? 'Visível no perfil'
                                : 'Oculto no perfil'}
                            </span>
                          </span>
                          <Icon
                            icon={
                              expanded.includes(id)
                                ? 'mdi:chevron-up'
                                : 'mdi:chevron-down'
                            }
                            aria-hidden
                          />
                        </button>
                        <label className={styles.activation}>
                          <input
                            type="checkbox"
                            role="switch"
                            aria-label={`Ativar selo ${name}`}
                            checked={messages[id].enabled === true}
                            disabled={saving}
                            onChange={(event) => {
                              setMessages((current) =>
                                current
                                  ? {
                                      ...current,
                                      [id]: {
                                        ...current[id],
                                        enabled: event.target.checked,
                                      },
                                    }
                                  : current
                              )
                              setSaved(false)
                            }}
                          />
                          {messages[id].enabled === true ? 'Ativo' : 'Inativo'}
                        </label>
                      </div>
                      {expanded.includes(id) && (
                        <div id={`badge-panel-${id}`} className={styles.card}>
                          <div className={styles.badgePane}>
                            <span className={styles.step}>
                              SELO {String(number).padStart(2, '0')}
                            </span>
                            <img src={image} alt="" />
                            <h3>{name}</h3>
                            <p>
                              <Icon icon="mdi:lock-open-check" aria-hidden />{' '}
                              {id === 'special_supporter'
                                ? `Acumule ${formatTargetAmount(savedTargetAmount)} em compras`
                                : milestone}
                            </p>
                            {id === 'special_supporter' && (
                              <div className={styles.targetSetting}>
                                <label htmlFor="special-supporter-target">
                                  Valor acumulado para conquistar (R$)
                                </label>
                                <input
                                  id="special-supporter-target"
                                  type="number"
                                  inputMode="decimal"
                                  min="0.01"
                                  max="9999999.99"
                                  step="0.01"
                                  value={targetAmount}
                                  disabled={saving}
                                  onChange={(event) => {
                                    setTargetAmount(event.target.value)
                                    setTargetNotice(null)
                                    setSaved(false)
                                  }}
                                  aria-describedby="special-supporter-target-help"
                                />
                                <small id="special-supporter-target-help">
                                  Padrão: R$ 1.000. Contabiliza as compras pagas nesta
                                  campanha. A nova meta será aplicada ao total já
                                  acumulado pelos clientes.
                                </small>
                                <button
                                  type="button"
                                  onClick={() => void saveTarget()}
                                  disabled={saving || !validTargetAmount(targetAmount)}
                                >
                                  {saving ? 'Salvando...' : 'Salvar valor'}
                                </button>
                                {targetNotice && (
                                  <span role="status">{targetNotice}</span>
                                )}
                              </div>
                            )}
                            {id === 'calendar' && (
                              <div className={styles.targetSetting}>
                                <label htmlFor="calendar-target-days">
                                  Dias seguidos para conquistar
                                </label>
                                <input
                                  id="calendar-target-days"
                                  type="number"
                                  inputMode="numeric"
                                  min="1"
                                  max="365"
                                  step="1"
                                  required
                                  value={messages.calendar.target_days ?? 7}
                                  disabled={saving}
                                  aria-describedby="calendar-target-help"
                                  onChange={(event) => {
                                    const target_days = Number(event.target.value)
                                    setMessages((current) =>
                                      current
                                        ? {
                                            ...current,
                                            calendar: {
                                              ...current.calendar,
                                              target_days,
                                            },
                                          }
                                        : current
                                    )
                                    setSaved(false)
                                    setError(null)
                                  }}
                                />
                                <small id="calendar-target-help">
                                  Padrão: 7 dias. Conta uma presença diária ao acessar o
                                  perfil completo, no horário de Brasília. Se faltar um
                                  dia, a sequência recomeça. O selo e os prêmios são
                                  entregues uma única vez por participante. Alterações na
                                  meta valem para sequências ainda não concluídas.
                                </small>
                              </div>
                            )}
                          </div>
                          <div className={styles.fields}>
                            <p className={styles.fieldsHeading}>Antes da conquista</p>
                            <label htmlFor={`${id}-locked-reason`}>
                              {id === 'scratch_card'
                                ? 'Convite para fazer a raspadinha'
                                : 'Motivo para conquistar o selo'}{' '}
                              <span>Opcional</span>
                            </label>
                            <textarea
                              id={`${id}-locked-reason`}
                              disabled={saving}
                              value={messages[id].locked_reason ?? ''}
                              onChange={(event) =>
                                updateField(id, 'locked_reason', event.target.value)
                              }
                              maxLength={2000}
                              rows={3}
                              placeholder="Vai receber uma mensagem de agradecimento e um vídeo exclusivo pela conquista do selo."
                            />
                            <small>
                              {id === 'profile_complete'
                                ? 'O selo de perfil leva diretamente ao cadastro enquanto estiver bloqueado.'
                                : 'Aparece no modal do selo ainda não conquistado. Se ficar vazio, será exibida a mensagem padrão.'}
                            </small>
                            <p className={styles.fieldsHeading}>
                              {id === 'scratch_card'
                                ? 'Mensagem de parabéns ao ganhar'
                                : 'Mensagem ao desbloquear'}
                            </p>
                            <label htmlFor={`${id}-title`}>
                              Título <span>Obrigatório</span>
                            </label>
                            <input
                              id={`${id}-title`}
                              disabled={saving}
                              value={messages[id].title}
                              onChange={(event) =>
                                updateField(id, 'title', event.target.value)
                              }
                              maxLength={120}
                              required
                              placeholder="Ex: Você fez a diferença!"
                            />
                            <label htmlFor={`${id}-text`}>
                              Texto de agradecimento <span>Opcional</span>
                            </label>
                            <textarea
                              id={`${id}-text`}
                              disabled={saving}
                              value={messages[id].text ?? ''}
                              onChange={(event) =>
                                updateField(id, 'text', event.target.value)
                              }
                              maxLength={4000}
                              rows={4}
                              placeholder="Conte ao apoiador por que essa conquista é especial..."
                            />
                            <label htmlFor={`${id}-video`}>
                              Link do vídeo no YouTube <span>Opcional</span>
                            </label>
                            <input
                              id={`${id}-video`}
                              disabled={saving}
                              type="url"
                              value={messages[id].video_url ?? ''}
                              onChange={(event) =>
                                updateField(id, 'video_url', event.target.value)
                              }
                              maxLength={500}
                              placeholder="https://www.youtube.com/watch?v=..."
                            />
                            <small>
                              Use um link público do YouTube. O vídeo aparecerá dentro do
                              selo.
                            </small>
                          </div>
                          <AchievementRewardsEditor
                            id={id}
                            value={messages[id].rewards ?? EMPTY_ACHIEVEMENT_REWARDS}
                            disabled={saving}
                            onChange={(rewards) => {
                              setMessages((current) =>
                                current
                                  ? { ...current, [id]: { ...current[id], rewards } }
                                  : current
                              )
                              setSaved(false)
                              setError(null)
                            }}
                          />
                          {id === 'scratch_card' && (
                            <>
                              <ScratchCardConfigEditor
                                value={scratch}
                                disabled={saving}
                                onChange={(value) => {
                                  setScratch(value)
                                  setSaved(false)
                                  setError(null)
                                }}
                              />
                              <ScratchCardRecipients
                                key={`${loadKey}:scratch`}
                                campaignId={campaignId}
                                token={token ?? undefined}
                              />
                            </>
                          )}
                          {id !== 'first_purchase' && id !== 'scratch_card' && (
                            <CampaignAchievementRecipients
                              key={`${loadKey}:${id}:${id === 'special_supporter' ? savedTargetAmount : ''}`}
                              campaignId={campaignId}
                              badge={id}
                              badgeName={name}
                              token={token ?? undefined}
                            />
                          )}
                        </div>
                      )}
                    </section>
                  )
                )}
              </div>
            </section>
          ))}
          <MonthlyMedalsAdmin
            messages={messages}
            saving={saving}
            onChange={(id, message) => {
              setMessages((current) =>
                current ? { ...current, [id]: message } : current
              )
              setSaved(false)
              setError(null)
            }}
          />
          <div className={styles.footer}>
            <span role="status">
              {error ||
                (saved
                  ? 'Configurações dos selos salvas com sucesso!'
                  : 'As alterações valem apenas para esta campanha.')}
            </span>
            <button type="submit" disabled={saving}>
              <Icon
                icon={saving ? 'mdi:loading' : 'mdi:content-save-outline'}
                aria-hidden
              />{' '}
              {saving ? 'Salvando...' : 'Salvar mensagens'}
            </button>
          </div>
        </form>
      ) : (
        <p className={styles.status} role="alert">
          {error ?? 'Não foi possível carregar os selos.'}
        </p>
      )}
    </div>
  )
}

function validTargetAmount(value: string): boolean {
  if (value === '') return true
  return (
    /^\d{1,7}(\.\d{1,2})?$/.test(value) &&
    Number(value) > 0 &&
    Number(value) <= 9999999.99
  )
}

function formatTargetAmount(value: string): string {
  return Number(value).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiClientError) {
    return Object.values(cause.errors ?? {}).flat()[0] ?? cause.message
  }
  return 'Não foi possível salvar as mensagens. Tente novamente.'
}
